import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { useRequestUrl, useCourt } from "../context/contextHooks";
import { statesLong } from "../global/utils";
import { focusWhenExposed } from "../global/focus";
import { useForm } from 'react-hook-form';
import {
    feeFromName,
    getFeeTotal,
    getPaymentRequest,
    joinFieldErrors,
    validateFeeEntry,
    validateFeeInfoFields
} from "./miscFeeFunctions";
import { useStyles } from '../styles/styles';
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery'
import API from "../http/API";
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from "@mui/material/Grid";
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Typography from "@mui/material/Typography";
import TextField from '@mui/material/TextField';
import { PatternFormat } from "react-number-format";
import Button from "@mui/material/Button";
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Backdrop from '@mui/material/Backdrop';
import ReplayIcon from "@mui/icons-material/Replay";
import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded';
import Drawer from '@mui/material/Drawer';
import AddFee from "./AddFee";
import FeeList from "./FeeList";
import SelectedFees from "./SelectedFees";
import CompletePayment from "../completePayment/CompletePayment";
import PaymentDetails from "../completePayment/PaymentDetails";
import PaymentsTwoToneIcon from "@mui/icons-material/PaymentsTwoTone";
import ManageWidget from "../widgetPay/ManageWidget";

const MiscFees = () => {
    const navigate = useNavigate();
    const [requestUrl,] = useRequestUrl();
    const { register, setValue, getValues } = useForm();
    const classes = useStyles;
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('xl'));
    const phone = useMediaQuery(theme.breakpoints.down('md'));
    const [court,] = useCourt();
    const [actionResponse, setActionResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    // Which controls the current errorMessage is about. Drives the per-field
    // aria-invalid and the focus move, neither of which the summary text can
    // express on its own (CPDEV-2789, 3.3.1).
    const [fieldErrors, setFieldErrors] = useState([]);
    const [feeData, setFeeData] = useState(null);
    const [selectedFees, setSelectedFees] = useState([]);
    const [selectedFee, setSelectedFee] = useState(null);
    const [allFees, setAllFees] = useState(null);
    const [deliveryOptions, setDeliveryOptions] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [showSelected, setShowSelected] = useState(false);
    const [openSelectedClicked, setOpenSelectedClicked] = useState(false);
    const [feeTotal, setFeeTotal] = useState(0);
    const [processingFee, setProcessingFee] = useState(0);
    const [openFees, setOpenFees] = useState(false);
    const [feeResponse, setFeeResponse] = useState(null);
    // The processing fee has its own response slot. On the shared one, a fee
    // answer that arrived while another request was loading was dropped, leaving
    // the fee for an earlier selection in place (CPDEV-2993). feeRequest numbers
    // each fee request so only the answer to the newest one is kept.
    const [feePending, setFeePending] = useState(0);
    const feeRequest = useRef(0);
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [paymentResult, setPaymentResult] = useState(null);
    const [openReceipt, setOpenReceipt] = useState(false);

    // Focus management for the Selected Fees drawer. The drawer has no single
    // opening control - it opens as a side effect of a fee or a delivery option
    // being selected - and "Add Fee" disables itself as part of the same action
    // that opens it. Focus is therefore returned to whichever control is actually
    // mounted and enabled once the drawer is gone, rather than to "the opener".
    const seeSelectedRef = useRef(null);
    const errorRef = useRef(null);
    const returningFromPayment = useRef(false);
    const FEE_SELECT_ID = 'feeSelect';
    const ERROR_ID = 'miscfees-error';

    // Only the control focus lands on describes itself with the summary Alert, so
    // the whole message is read on arrival. Returning undefined elsewhere leaves
    // no dangling IDREF - React omits an attribute whose value is undefined.
    const firstErrorField = fieldErrors.find((e) => e.field)?.field;
    const isInvalid = (field) => (fieldErrors.some((e) => e.field === field) ? 'true' : 'false');
    const describedBy = (field) => (field === firstErrorField ? ERROR_ID : undefined);

    // Every focus move on this screen follows a modal closing - the Selected Fees
    // drawer, or the payment dialog - so all of them wait for the target to be
    // exposed again. See global/focus.js for why the plain deferral was not enough.
    const focusSeeSelected = () => focusWhenExposed(() => seeSelectedRef.current);

    const focusFeeSelect = () => focusWhenExposed(() => document.getElementById(FEE_SELECT_ID));

    const clearMessages = () => {
        setErrorMessage(null);
        setFieldErrors([]);
        setActionResponse(null);
    }

    const reportErrors = (errors) => {
        setFieldErrors(errors);
        setErrorMessage(joinFieldErrors(errors));
    }

    const getFees = () => {
        clearMessages();
        setLoading(true);
        const url = `${requestUrl}/miscFees/${court.courtId}`;
        API.getRetrieve(url, 'miscFees', null, setActionResponse);
    }

    const getProcessingFee = (orgAmount) => {
        clearMessages();
        const seq = ++feeRequest.current;
        setFeePending(n => n + 1);
        const query = {
            courtId: court.courtId,
            paymentCategory: 'Online',
            paymentType: 'Card',
            courtAmount: orgAmount
        }
        const url = `${requestUrl}/calculateFee`;
        API.postAction(url, JSON.stringify(query), 'getProcessingFee', null, (response) => {
            setFeePending(n => n - 1);
            if (seq !== feeRequest.current) return;
            if (response.error) setErrorMessage(response.errorMessage);
            else setFeeResponse(response);
        });
    }

    // Sets the fee without asking the server, and retires any fee request still
    // in flight so its answer cannot land on top.
    const resetFee = (value) => {
        feeRequest.current++;
        setFeeResponse(value);
    }

    const feeClicked = (e) => {
        const feeName = e.target.value;
        if (feeName) {
            setSelectedFee(feeFromName(allFees, feeName));
        } else {
            setSelectedFee(null);
        }
        setValue('feeSelect', '');
    }

    // Returns whether the fee was accepted, so AddFee only clears its entry on
    // success. Clearing on failure wiped the fields the error was asking the user
    // to correct, and disabled the Add Fee button along with them.
    const addSelected = (fee) => {
        clearMessages();
        const errors = validateFeeEntry(fee, selectedFee);
        if (errors.length > 0) {
            reportErrors(errors);
            return false;
        }
        setSelectedFees([...selectedFees, fee]);
        setSelectedFee(null);
        return true;
    }

    const removeSelected = (key) => {
        const fees = selectedFees.filter(f => f.key !== key);
        setSelectedFees(fees);
        if (fees && fees.length > 0) {
            const total = getFeeTotal(fees, selectedOption);
            setFeeTotal(total);
            getProcessingFee(total);
        } else {
            setFeeTotal(0);
            resetFee({citepayFee: 0, courtAmount: 0, totalAmount: 0})
            // Removing the last item closes the drawer, so keep focus on the page.
            if (!selectedOption) focusFeeSelect();
        }
    }

    const clearSelected = () => {
        selectedFees.forEach(fee => removeSelected(fee.key));
        setSelectedFees([]);
        setSelectedOption(null);
        setValue('optionSelect', '');
    }

    // Clearing empties the drawer, which closes it. "See Selected" is not rendered
    // and "Add Fee" is disabled, so focus goes to the start of the Add a Fee flow.
    const clearAndClose = () => {
        clearSelected();
        focusFeeSelect();
    }

    const optionClicked = (e) => {
        const feeName = e.target.value;
        const option = feeFromName(deliveryOptions, feeName);
        setSelectedOption(option);
        const total = getFeeTotal(selectedFees, option);
        setFeeTotal(total);
        getProcessingFee(total);
    }

    const somethingSelected = () => {
        return ((selectedFees && selectedFees.length > 0) || (selectedOption));
    }


    const payClicked = () => {
        setShowSelected(false);
        clearMessages();
        const form = getValues();
        const errors = validateFeeInfoFields(selectedFees, selectedOption, form, court);
        if (errors.length > 0) {
            reportErrors(errors);
        } else {
            returningFromPayment.current = true;
            setPaymentRequest(getPaymentRequest(court.courtId, selectedFees, selectedOption, form, feeTotal, processingFee));
        }
    }

    const openSelected = () => {
        setOpenSelectedClicked(true);
    }

    // Back button inside the drawer. Fees are still selected, so "See Selected"
    // is rendered by the time the drawer closes and re-opens the same panel.
    const backFromSelected = () => {
        setShowSelected(false);
        focusSeeSelected();
    }

    const toggleSelected = (open) =>
        (event) => {
            if (
                event.type === 'keydown' &&
                (event.key === 'Tab' || event.key === 'Shift')
            ) {
                return;
            }
            setShowSelected(open);
            // Backdrop click or Escape: same target as the Back button.
            if (!open) focusSeeSelected();
        }

    // 3.3.1/2.4.3: put the user on the first field at fault rather than leaving
    // focus where they submitted from, with the summary Alert out of reach. That
    // field points aria-describedby at the Alert while it is the one in error, so
    // the description is read out on arrival. Errors that belong to no single
    // control - nothing selected, over the transaction limit - have nothing to
    // focus, so the Alert itself takes focus as it did before.
    useEffect(() => {
        if (fieldErrors.length < 1) return;
        const target = fieldErrors.find((e) => e.field);
        if (!target) {
            focusWhenExposed(() => errorRef.current);
            return;
        }
        focusWhenExposed(() => {
            // Not every id belongs to something focusable, so fall back to the
            // focusable descendant and then to the shared name attribute.
            const holder = document.getElementById(target.field);
            const focusable = holder && (holder.matches('input, select, textarea')
                ? holder
                : holder.querySelector('input, select, textarea'));
            return focusable || document.querySelector(`[name="${target.field}"]`);
        });
    }, [fieldErrors]);

    useEffect(() => {
        document.title = `Miscellaneous Fees - Neumo Payments`;
    }, []);

    useEffect(() => {
        const courtName = court?.name || 'Court';
        document.title = `Miscellaneous Fees - ${courtName} - CitePayUSA`;
    }, [court]);

    useEffect(() => {
        if (loading && actionResponse) {
            if (!actionResponse.error) {
                if (actionResponse.requestType === 'miscFees') {
                    setFeeData(actionResponse.data);
                    if (actionResponse.data.miscFees) {
                        setAllFees(actionResponse.data.miscFees);
                    }
                    if (actionResponse.data.deliveryOptions) {
                        setDeliveryOptions(actionResponse.data.deliveryOptions);
                    }
                }
            }
            setLoading(false);
        }
    }, [actionResponse])

    useEffect(() => {
        if (!feeData) getFees();
    }, [feeData])

    useEffect(() => {
        if ((selectedFees && selectedFees.length > 0) || selectedOption) {
            setShowSelected(true);
            const feeTot = getFeeTotal(selectedFees, selectedOption);
            getProcessingFee(feeTot);
            setFeeTotal(feeTot);
        } else {
            resetFee({citepayFee: 0, courtAmount: 0, totalAmount: 0})
            setFeeTotal(0);
            setProcessingFee(0);
            setShowSelected(false);
        }
        setOpenSelectedClicked(false);
    }, [selectedFees, selectedOption, openSelectedClicked])

    useEffect(() => {
        if (feeResponse) {
            setProcessingFee(feeResponse.citepayFee);
        }
    }, [feeResponse])

    useEffect(() => {
        if (paymentRequest) {
            setOpenDialog(true);
        }
    }, [paymentRequest])

    useEffect(() => {
        if (paymentResult) {
            setOpenReceipt(true);
        }
    }, [paymentResult])

    useEffect(() => {
        if (!openReceipt && paymentResult) {
            setSelectedFees([]);
            setSelectedOption(null);
            setSelectedFee(null);
            setFeeTotal(0);
            setProcessingFee(0);
            setPaymentRequest(null);
            setPaymentResult(null);
        }
    }, [openReceipt])

    // Returning from the payment dialog (cancelled, or after the receipt is closed).
    // The drawer was closed by payClicked and the Pay button went with it, so focus
    // lands on "See Selected" if fees are still selected and on the fee list if not.
    useEffect(() => {
        if (returningFromPayment.current && !openDialog && !paymentResult) {
            returningFromPayment.current = false;
            if (somethingSelected()) focusSeeSelected();
            else focusFeeSelect();
        }
    }, [openDialog, paymentResult])

    return (
        <>
            { paymentRequest && !paymentResult &&
                <>
                    { court.neumoPayOnline ?
                        <ManageWidget
                            openDialog={openDialog}
                            setOpenDialog={setOpenDialog}
                            paymentRequest={paymentRequest}
                            setPaymentRequest={setPaymentRequest}
                            setPaymentResult={setPaymentResult}
                            court={court}
                            feeResponse={feeResponse}
                        />
                        :
                        <CompletePayment
                            openDialog={openDialog}
                            setOpenDialog={setOpenDialog}
                            paymentRequest={paymentRequest}
                            setPaymentRequest={setPaymentRequest}
                            setPaymentResult={setPaymentResult}
                            court={court}
                        />

                    }
                </>
            }
            { paymentResult &&
                <PaymentDetails
                    paymentRequest={paymentRequest}
                    paymentResult={paymentResult}
                    openReceipt={openReceipt}
                    setOpenReceipt={setOpenReceipt}
                    court={court}
                />
            }
            { openFees &&
                <FeeList openFees={openFees} setOpenFees={setOpenFees} fees={allFees} />
            }
            { errorMessage && phone &&
                <Alert id={ERROR_ID} ref={errorRef} tabIndex={-1} severity='error' sx={{marginTop: '1rem', textAlign: 'left'}}>
                    <AlertTitle>Error!</AlertTitle>
                    {errorMessage}
                </Alert>
            }
            {phone && (
                <AddFee payableFee={selectedFee} addSelected={addSelected} allFees={allFees} feeClicked={feeClicked}
                    fieldErrors={fieldErrors} errorId={ERROR_ID} />
            )}
            <Typography variant={smallScreen ? 'h6' : 'h5'} component="h2" color='primary' sx={{...classes.title, marginTop: '1rem'}}>
                Pay Non-Case Fees
            </Typography>
            <Grid
                container
                spacing={3}
                sx={{
                    justifyContent: "center"
                }}
            >
                <Backdrop open={loading || feePending > 0} sx={classes.backdrop}>
                    <CircularProgress color='inherit' />
                </Backdrop>
                <Grid
                    sx={classes.gridBottom}
                    size={{
                        xs: 12,
                        md: 4
                    }}>
                    <Paper elevation={3} sx={classes.controlPanel}>
                        <Grid container spacing={1} sx={{
                            alignItems: 'top'
                        }}>
                            { deliveryOptions &&
                                <Grid size={12}>
                                    <FormControl
                                        component="fieldset"
                                        fullWidth
                                        sx={classes.formControl}
                                        {...register('optionSelect')}
                                    >
                                        <FormLabel component="legend" htmlFor='optionSelect'>
                                            {/* Not a second <label>: the wrapping FormLabel already
                                                associates this text with the select via htmlFor. This
                                                one carried `for` rather than `htmlFor`, so React
                                                dropped the attribute and it was a nameless duplicate
                                                label inside the legend. */}
                                            <Typography variant="subtitle1" component='span' color='primary'>
                                                Select a Delivery Option
                                            </Typography>
                                        </FormLabel>
                                        <Select
                                            name='optionSelect'
                                            native
                                            variant='outlined'
                                            fullWidth
                                            sx={{height: '2rem'}}
                                            onChange={optionClicked}
                                            // aria-label because the visible text sits in a <legend>,
                                            // and `for` is ignored on a legend, so it names the
                                            // fieldset rather than this select. Same shape as
                                            // feeSelect in AddFee.
                                            inputProps={{id: 'optionSelect', 'aria-label': 'Select a Delivery Option'}}
                                        >
                                            <option key='-1' value=''>None</option>
                                            {deliveryOptions && deliveryOptions.map((option) => (
                                                <option key={option.name} value={option.name} style={{fontSize: '.9rem'}}>{option.name}</option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            }
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('payorName')}
                                >
                                    <FormLabel htmlFor='payorName' sx={classes.inputLabel}>Your Name *</FormLabel>
                                    <TextField
                                        id='payorName'
                                        name='payorName'
                                        variant='outlined'
                                        fullWidth
                                        slotProps={{
                                            htmlInput: {
                                                sx: classes.textbox,
                                                "aria-required": "true",
                                                "aria-invalid": isInvalid('payorName'),
                                                "aria-describedby": describedBy('payorName'),
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('partyName')}
                                >
                                    <FormLabel htmlFor='partyName' sx={classes.inputLabel}>Party Name</FormLabel>
                                    <TextField
                                        id='partyName'
                                        name='partyName'
                                        variant='outlined'
                                        fullWidth
                                        slotProps={{
                                            htmlInput: {sx: classes.textbox}
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('address')}
                                >
                                    <FormLabel htmlFor='address' sx={classes.inputLabel}>Billing Address *</FormLabel>
                                    <TextField
                                        id='address'
                                        name='address'
                                        variant='outlined'
                                        fullWidth
                                        slotProps={{
                                            htmlInput: {
                                                sx: classes.textbox,
                                                "aria-required": "true",
                                                "aria-invalid": isInvalid('address'),
                                                "aria-describedby": describedBy('address'),
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('city')}
                                >
                                    <FormLabel htmlFor='city' sx={classes.inputLabel}>City *</FormLabel>
                                    <TextField
                                        id='city'
                                        name='city'
                                        variant='outlined'
                                        fullWidth
                                        slotProps={{
                                            htmlInput: {
                                                sx: classes.textbox,
                                                "aria-required": "true",
                                                "aria-invalid": isInvalid('city'),
                                                "aria-describedby": describedBy('city'),
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('state')}
                                >
                                    <FormLabel required htmlFor='state' sx={classes.inputLabel}>State </FormLabel>
                                    <Select
                                        id='state'
                                        name='state'
                                        native
                                        variant='outlined'
                                        fullWidth
                                        sx={{height: '2rem'}}
                                        inputProps={{
                                            id: 'state',
                                            "aria-required": "true",
                                            "aria-label": "State",
                                            "aria-invalid": isInvalid('state'),
                                            "aria-describedby": describedBy('state'),
                                        }}
                                    >
                                        <option key='None' value=''>Select your state</option>
                                        {statesLong.map((state) => (
                                            <option key={state.key} value={state.value} style={{fontSize: '.9rem'}}>{state.text}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('zip')}
                                >
                                    <FormLabel htmlFor='zip' sx={classes.inputLabel} >Billing Zip *</FormLabel>
                                    <PatternFormat
                                        id='zip'
                                        name='zip'
                                        customInput={TextField}
                                        type='text'
                                        autoComplete='off'
                                        variant='outlined'
                                        format='#####'
                                        slotProps={{
                                            input: {
                                                style: classes.numberbox
                                            },

                                            htmlInput: {
                                                "aria-required": "true",
                                                "aria-invalid": isInvalid('zip'),
                                                "aria-describedby": describedBy('zip'),
                                            }
                                        }} />
                                </FormControl>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('email')}
                                >
                                    <FormLabel htmlFor='email' sx={classes.inputLabel}>Email Address</FormLabel>
                                    <TextField
                                        id='email'
                                        name='email'
                                        variant='outlined'
                                        fullWidth
                                        slotProps={{
                                            htmlInput: {
                                                sx: classes.textbox,
                                                "aria-invalid": isInvalid('email'),
                                                "aria-describedby": describedBy('email'),
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <FormControl
                                    fullWidth
                                    sx={classes.formControl}
                                    {...register('phone')}
                                >
                                    <FormLabel htmlFor='phone' sx={classes.inputLabel}>Phone Number</FormLabel>
                                    <PatternFormat
                                        id='phone'
                                        name='phone'
                                        customInput={TextField}
                                        type='text'
                                        autoComplete='off'
                                        variant='outlined'
                                        format="(###) ###-####"
                                        slotProps={{
                                            input: {
                                                style: classes.numberbox
                                            },
                                            htmlInput: {
                                                "aria-invalid": isInvalid('phone'),
                                                "aria-describedby": describedBy('phone'),
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid size={12}>
                                <Typography variant='body2' component='Grid'>
                                    * indicates a required field.  Either an email address or a phone number are also required.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                    {/* DOM order is the visual reading order (2.4.3). row-reverse +
                        wrap-reverse read See Selected, Back, List Fees both on one
                        line and wrapped, so that is the DOM order now; justifyContent
                        keeps the row right-aligned as before.
                        Each of these opens a modal dialog - the Selected Fees
                        drawer, the fee list, the payment dialog - so each says so
                        with aria-haspopup (4.1.2). Pay only reaches the dialog when
                        the form validates, but the popup is still what it offers. */}
                    <Box
                        component='div'
                        sx={{
                            flexWrap: 'wrap',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            margin: '1rem',
                            marginBottom: '0'
                        }}>
                        { somethingSelected() && !showSelected &&
                            <Button
                                ref={seeSelectedRef}
                                variant='contained'
                                color='primary'
                                startIcon={!smallScreen && <PlaylistAddCheckRoundedIcon />}
                                sx={{...classes.squashedButton, whiteSpace: 'nowrap', fontSize: {xs: '.7rem', xl: '.85rem'}}}
                                onClick={openSelected}
                                aria-haspopup="dialog"
                            >
                                See Selected
                            </Button>
                        }
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={!smallScreen && <ReplayIcon/>}
                            sx={{...classes.squashedButton, whiteSpace: 'nowrap', fontSize: {xs: '.7rem', xl: '.85rem'}}}
                            onClick={() => navigate(-1)}
                        >
                            Back
                        </Button>
                        <Button
                            variant='contained'
                            color='primary'
                            startIcon={!smallScreen && <ListAltTwoToneIcon />}
                            sx={{...classes.squashedButton, whiteSpace: 'nowrap', fontSize: {xs: '.7rem', xl: '.85rem'}}}
                            onClick={() => setOpenFees(true)}
                            aria-haspopup="dialog"
                        >
                            List Fees
                        </Button>
                    </Box>
                    { selectedFees && selectedFees.length > 0 && feeTotal > 0 &&
                        <Box
                            component='div'
                            sx={{
                                flexWrap: 'wrap-reverse',
                                display: 'flex',
                                flexDirection: 'row-reverse'
                            }}>
                            <Button
                                variant='contained'
                                fullWidth
                                color='primary'
                                startIcon={smallScreen ? null : <PaymentsTwoToneIcon/>}
                                sx={{...classes.squashedButton, whiteSpace: 'nowrap', fontSize: {xs: '.7rem', xl: '.85rem'}}}
                                onClick={() => payClicked()}
                                aria-haspopup="dialog"
                            >
                                Pay Selected Fee(s)
                            </Button>
                        </Box>
                    }
                </Grid>
                <Grid
                    style={{paddingTop: phone ? '0' : '1.5rem'}}
                    size={{
                        xs: 12,
                        md: 8
                    }}>
                    <Drawer
                        anchor='right'
                        open={showSelected}
                        onClose={toggleSelected(false)}
                        slotProps={{
                            // The drawer is a modal dialog; without a name it is
                            // announced as an unlabelled dialog.
                            paper: {
                                'aria-label': 'Selected Fees',
                                style: {width: smallScreen ? '70vw' : '50vw', margin: '1rem', padding: '1rem'}
                            }
                        }}
                    >
                        <SelectedFees
                            fees={selectedFees}
                            selectedOption={selectedOption}
                            removeSelected={removeSelected}
                            clearSelected={clearAndClose}
                            feeTotal={feeTotal}
                            processingFee={processingFee}
                            splitFees={court.onlineCheckFeeConfig && !court.noChex}
                            onBack={backFromSelected}
                            payClicked={payClicked}
                        />
                    </Drawer>
                    { errorMessage && !phone &&
                        <Alert id={ERROR_ID} ref={errorRef} tabIndex={-1} severity='error' sx={{marginTop: '1rem', textAlign: 'left'}}>
                            <AlertTitle>Error!</AlertTitle>
                            {errorMessage}
                        </Alert>
                    }
                    {!phone && (
                        <AddFee payableFee={selectedFee} addSelected={addSelected} allFees={allFees} feeClicked={feeClicked}
                                fieldErrors={fieldErrors} errorId={ERROR_ID} />
                    )}
                </Grid>
            </Grid>
        </>
    );
}

export default MiscFees;
